// src/pages/Employees/components/employeeFormPanelMeta.jsx
//
// Shared metadata (icon + one-line description + category grouping) for
// every top-level panel in EmployeeForm.jsx's Collapse. Used both by
// EmployeeFormQuickNav.jsx (category chips) and EmployeeForm.jsx itself
// (per-panel header enrichment) so the two stay in sync.
import React from "react";
import {
  UserOutlined,
  SolutionOutlined,
  WalletOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  LogoutOutlined,
  AimOutlined,
  CalendarOutlined,
  BankOutlined,
  HistoryOutlined,
  TrophyOutlined,
  ReadOutlined,
  MedicineBoxOutlined,
  FolderOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";

export const PANEL_CATEGORIES = [
  { key: "personal_contact", label: "Personal & Contact", icon: <UserOutlined />, firstPanelKey: "personal" },
  { key: "job_career", label: "Job & Career", icon: <SolutionOutlined />, firstPanelKey: "job" },
  { key: "compensation_leave", label: "Compensation & Leave", icon: <WalletOutlined />, firstPanelKey: "salary" },
  { key: "compliance_conduct", label: "Compliance & Conduct", icon: <SafetyCertificateOutlined />, firstPanelKey: "disciplinary_legal" },
  { key: "client_other", label: "Client & Other", icon: <TeamOutlined />, firstPanelKey: "client_project" },
  { key: "exit_offboarding", label: "Exit & Offboarding", icon: <LogoutOutlined />, firstPanelKey: "exit_termination_info" },
];

// Keyed by the exact Panel `key` prop already used in EmployeeForm.jsx.
export const PANEL_META = {
  personal: { icon: <UserOutlined />, description: "Contact details, ID documents, emergency contacts", category: "personal_contact" },
  job: { icon: <SolutionOutlined />, description: "Joining date, department, designation, employment type, custom weekend days", category: "job_career" },
  salary: { icon: <WalletOutlined />, description: "Salary components, allowances, payslip generator", category: "compensation_leave" },
  sales_target: { icon: <AimOutlined />, description: "Sales target and commission percentage", category: "compensation_leave" },
  client_project: { icon: <TeamOutlined />, description: "Client assignment, timesheet and attendance approval", category: "client_other" },
  leaves_info: { icon: <CalendarOutlined />, description: "Annual/Casual, Sick, and Flexi Leave balances", category: "compensation_leave" },
  gratuity_estimate: { icon: <BankOutlined />, description: "Estimated end-of-service gratuity", category: "compensation_leave" },
  payroll_info: { icon: <BankOutlined />, description: "Bank account and payment details", category: "compensation_leave" },
  work_experience: { icon: <HistoryOutlined />, description: "Previous employers and roles", category: "job_career" },
  performance_management: { icon: <TrophyOutlined />, description: "Performance reviews, goals, feedback", category: "job_career" },
  disciplinary_legal: { icon: <SafetyCertificateOutlined />, description: "Disciplinary records and legal documents", category: "compliance_conduct" },
  training_development: { icon: <ReadOutlined />, description: "Courses, certifications, training plans", category: "job_career" },
  health_safety_info: { icon: <MedicineBoxOutlined />, description: "Medical records, health and safety assessments", category: "compliance_conduct" },
  other_information: { icon: <FolderOutlined />, description: "Company assets, HR documents, other links", category: "client_other" },
  exit_termination_info: { icon: <LogoutOutlined />, description: "Exit details, final settlement, offboarding", category: "exit_offboarding" },
  application_settings_user_mapping: { icon: <UserSwitchOutlined />, description: "Map this employee to a user login account", category: "client_other" },
};

// Renders "<Icon/> <b>{title}</b><br/><small>{description}</small>" while
// keeping the original title text as a plain, findable text node — the
// Playwright suite clicks panel headers via getByText(title), which does
// substring matching, so it still resolves correctly with this richer markup.
//
// Collapse.Panel headers accept any ReactNode, so the category label (for
// the first panel of each group) is rendered as a stacked line INSIDE this
// same header rather than as a sibling element — Collapse's children must
// all be Panels, so a raw divider element between Panels isn't safe.
export const PanelHeader = ({ panelKey, title, required = false, categoryKey = null }) => {
  const meta = PANEL_META[panelKey];
  const category = categoryKey ? PANEL_CATEGORIES.find((c) => c.key === categoryKey) : null;

  return (
    <div>
      {category && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: "#8c8c8c",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          {category.label}
        </div>
      )}
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {meta?.icon && <span style={{ fontSize: 16, color: "#1677ff" }}>{meta.icon}</span>}
        <span>
          <span className={required ? "mandatory-panel-header" : undefined} style={{ fontWeight: 600 }}>
            {title} {required && <span className="mandatory-star">*</span>}
          </span>
          {meta?.description && (
            <>
              <br />
              <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>{meta.description}</span>
            </>
          )}
        </span>
      </span>
    </div>
  );
};
